import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import API from '../api/axios';
import { toast } from 'react-toastify';
import { FiEdit2, FiTrash2, FiPlus, FiX, FiCamera, FiEye } from 'react-icons/fi';
import './StyleMaster.css';
import './StockMaster.css';

function fmtINR(n) { if (!n && n !== 0) return '—'; return '₹' + Number(n).toLocaleString('en-IN'); }

const SERVER_ORIGIN = (process.env.NODE_ENV === 'production'
  ? 'https://creativeglassworld.com'
  : 'http://localhost:9119');

export default function StockMaster() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [viewItem, setViewItem] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const r = await API.get('/stock-items');
      setItems(r.data);
    } catch { toast.error('Failed to load stock items'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  async function handleDelete(id) {
    try {
      await API.delete(`/stock-items/${id}`);
      setConfirmDel(null);
      load();
      toast.success('Stock item deleted');
    } catch { toast.error('Error deleting item'); }
  }

  const totalValue = items.reduce((s, i) => s + (+i.qty || 0) * (+i.rate || 0), 0);

  return (
    <Layout>
      <div className="page">
        <div className="page-actions" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button className="sm-add-btn" onClick={() => { setEditItem(null); setShowModal(true); }}>
            <FiPlus size={15} /> Add Stock Item
          </button>
          {items.length > 0 && (
            <span style={{ fontSize: '.85rem', color: '#64748b' }}>
              Total Stock Value: <strong style={{ color: '#16a34a' }}>{fmtINR(totalValue)}</strong>
            </span>
          )}
        </div>

        <div className="sm-card" style={{ maxWidth: 'none' }}>
          {loading ? (
            <div className="sm-empty">Loading...</div>
          ) : items.length === 0 ? (
            <div className="sm-empty">No stock items yet. Click "+ Add Stock Item" to create one.</div>
          ) : (
            <table className="sm-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Photo</th>
                  <th>Item Code</th>
                  <th>Item Name</th>
                  <th>Height (ft)</th>
                  <th>Width (ft)</th>
                  <th>Total Area (SQ/FT)</th>
                  <th>Qty</th>
                  <th>Rate (₹)</th>
                  <th>Value (₹)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={it._id}>
                    <td className="sm-num">{i + 1}</td>
                    <td>
                      {it.photo
                        ? <img src={`${SERVER_ORIGIN}${it.photo}`} alt={it.itemName} className="sm-thumb" />
                        : <span className="sm-thumb-placeholder">—</span>}
                    </td>
                    <td>{it.itemCode || '–'}</td>
                    <td className="sm-name">{it.itemName}</td>
                    <td>{it.heightFt ?? '–'}</td>
                    <td>{it.widthFt ?? '–'}</td>
                    <td>{(it.heightFt && it.widthFt) ? (it.heightFt * it.widthFt).toLocaleString('en-IN') : '–'}</td>
                    <td>{it.qty}</td>
                    <td>{fmtINR(it.rate)}</td>
                    <td>{fmtINR((+it.qty || 0) * (+it.rate || 0))}</td>
                    <td>
                      <div className="sm-actions">
                        <button className="sm-icon-btn" onClick={() => setViewItem(it)}>
                          <FiEye size={14} />
                        </button>
                        <button className="sm-icon-btn sm-edit" onClick={() => { setEditItem(it); setShowModal(true); }}>
                          <FiEdit2 size={14} />
                        </button>
                        <button className="sm-icon-btn sm-del" onClick={() => setConfirmDel(it)}>
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showModal && (
        <StockItemModal
          item={editItem}
          onClose={() => { setShowModal(false); setEditItem(null); }}
          onSaved={() => { setShowModal(false); setEditItem(null); load(); toast.success(editItem ? 'Stock item updated' : 'Stock item added'); }}
        />
      )}

      {viewItem && (
        <div className="mp-overlay" onClick={() => setViewItem(null)}>
          <div className="mp-modal" onClick={e => e.stopPropagation()}>
            <div className="mp-modal-header">
              <h3>Stock Item Details</h3>
              <button className="mp-modal-close" onClick={() => setViewItem(null)}><FiX /></button>
            </div>
            <div className="mp-modal-body">
              <div className="sm-view-photo-wrap">
                {viewItem.photo
                  ? <img src={`${SERVER_ORIGIN}${viewItem.photo}`} alt={viewItem.itemName} className="sm-view-photo" />
                  : <span className="sm-view-photo-placeholder"><FiCamera size={26} /></span>}
              </div>
              <div className="sm-view-grid">
                <div><span>Item Code</span><strong>{viewItem.itemCode || '—'}</strong></div>
                <div><span>Item Name</span><strong>{viewItem.itemName}</strong></div>
                <div><span>Height (ft)</span><strong>{viewItem.heightFt ?? '—'}</strong></div>
                <div><span>Width (ft)</span><strong>{viewItem.widthFt ?? '—'}</strong></div>
                <div><span>Total Area (SQ/FT)</span><strong>{(viewItem.heightFt && viewItem.widthFt) ? (viewItem.heightFt * viewItem.widthFt).toLocaleString('en-IN') : '—'}</strong></div>
                <div><span>Quantity</span><strong>{viewItem.qty}</strong></div>
                <div><span>Rate</span><strong>{fmtINR(viewItem.rate)}</strong></div>
                <div><span>Stock Value</span><strong style={{ color: '#16a34a' }}>{fmtINR((+viewItem.qty || 0) * (+viewItem.rate || 0))}</strong></div>
              </div>
            </div>
            <div className="mp-modal-footer">
              <button className="mp-btn-ghost" onClick={() => setViewItem(null)}>Close</button>
              <button className="mp-btn-primary" onClick={() => { setEditItem(viewItem); setViewItem(null); setShowModal(true); }}>
                <FiEdit2 size={13} style={{ marginRight: 5 }} /> Edit Item
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="mp-overlay" onClick={() => setConfirmDel(null)}>
          <div className="mp-confirm-box" onClick={e => e.stopPropagation()}>
            <h4 className="mp-confirm-title">Delete Stock Item</h4>
            <p className="mp-confirm-msg">Delete "{confirmDel.itemName}"? This cannot be undone.</p>
            <div className="mp-confirm-actions">
              <button className="mp-confirm-cancel" onClick={() => setConfirmDel(null)}>Cancel</button>
              <button className="mp-confirm-ok" style={{ background: '#dc2626' }} onClick={() => handleDelete(confirmDel._id)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

function StockItemModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState({
    itemCode: item?.itemCode || '',
    itemName: item?.itemName || '',
    heightFt: item?.heightFt ?? '',
    widthFt:  item?.widthFt  ?? '',
    qty:      item?.qty      ?? '',
    rate:     item?.rate     ?? '',
  });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(item?.photo ? `${SERVER_ORIGIN}${item.photo}` : null);
  const [saving, setSaving] = useState(false);
  const NUMERIC_FIELDS = ['heightFt', 'widthFt', 'qty', 'rate'];
  const set = (k, v) => {
    if (NUMERIC_FIELDS.includes(k) && v !== '' && +v < 0) v = '0';
    setForm(f => ({ ...f, [k]: v }));
  };
  const areaPreview = (+(form.heightFt) || 0) * (+(form.widthFt) || 0);
  const valuePreview = (+(form.qty) || 0) * (+(form.rate) || 0);

  function onPhotoChange(e) {
    const file = e.target.files?.[0] || null;
    setPhoto(file);
    setPhotoPreview(file ? URL.createObjectURL(file) : (item?.photo ? `${SERVER_ORIGIN}${item.photo}` : null));
  }
  function clearPhoto() {
    setPhoto(null);
    setPhotoPreview(null);
  }

  async function save() {
    if (!form.itemName.trim()) return toast.error('Item name is required');
    if (NUMERIC_FIELDS.some(k => +(form[k]) < 0)) return toast.error('Negative values are not allowed');
    setSaving(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      if (photo) data.append('photo', photo);
      else if (item?.photo && !photoPreview) data.append('removePhoto', 'true');
      if (item) await API.put(`/stock-items/${item._id}`, data);
      else await API.post('/stock-items', data);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
    finally { setSaving(false); }
  }

  return (
    <div className="mp-overlay">
      <div className="mp-modal" onClick={e => e.stopPropagation()}>
        <div className="mp-modal-header">
          <h3>{item ? 'Edit Stock Item' : 'Add Stock Item'}</h3>
          <button className="mp-modal-close" onClick={onClose}><FiX /></button>
        </div>
        <div className="mp-modal-body">
          <div className="mp-mrow">
            <div className="mp-mgroup">
              <label>Item Code <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
              <input value={form.itemCode} onChange={e => set('itemCode', e.target.value)} placeholder="e.g. STK-001" />
            </div>
            <div className="mp-mgroup mp-mfull">
              <label>Item Name *</label>
              <input value={form.itemName} onChange={e => set('itemName', e.target.value)} placeholder="e.g. Toughened Glass Sheet" autoFocus />
            </div>
          </div>
          <div className="mp-mrow">
            <div className="mp-mgroup mp-mfull">
              <label>Photo <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional)</span></label>
              <div className="sm-photo-picker">
                <label className="sm-photo-tile">
                  <input type="file" accept="image/*" onChange={onPhotoChange} hidden />
                  {photoPreview
                    ? <img src={photoPreview} alt="Preview" className="sm-photo-tile-img" />
                    : <span className="sm-photo-tile-empty"><FiCamera size={20} /><span>Upload Photo</span></span>}
                </label>
                {photoPreview && (
                  <div className="sm-photo-meta">
                    {photo && <p className="sm-photo-filename">{photo.name}</p>}
                    <button type="button" className="sm-photo-remove" onClick={clearPhoto}><FiX size={12}/> Remove</button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mp-mrow">
            <div className="mp-mgroup">
              <label>Height (ft)</label>
              <input type="number" min="0" step="0.01" value={form.heightFt} onChange={e => set('heightFt', e.target.value)} placeholder="0" />
            </div>
            <div className="mp-mgroup">
              <label>Width (ft)</label>
              <input type="number" min="0" step="0.01" value={form.widthFt} onChange={e => set('widthFt', e.target.value)} placeholder="0" />
            </div>
          </div>
          {areaPreview > 0 && (
            <div className="mp-amount-preview">
              Total Area: <strong>{areaPreview.toLocaleString('en-IN')} SQ/FT</strong>
            </div>
          )}
          <div className="mp-mrow">
            <div className="mp-mgroup">
              <label>Quantity</label>
              <input type="number" min="0" value={form.qty} onChange={e => set('qty', e.target.value)} placeholder="0" />
            </div>
            <div className="mp-mgroup">
              <label>Rate (₹)</label>
              <input type="number" min="0" value={form.rate} onChange={e => set('rate', e.target.value)} placeholder="0" />
            </div>
          </div>
          {valuePreview > 0 && (
            <div className="mp-amount-preview">
              Stock Value: <strong>₹{valuePreview.toLocaleString('en-IN')}</strong>
            </div>
          )}
        </div>
        <div className="mp-modal-footer">
          <button className="mp-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="mp-btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving…' : item ? 'Update Item' : 'Add Item'}
          </button>
        </div>
      </div>
    </div>
  );
}
